/* eslint-disable react/no-unknown-property */
import React, { useMemo, useEffect, Suspense } from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { useAppSelector } from '@store/selectors';

function Scene({
	selectedProduct,
	selected2DArea,
	selectedCategory,
	selectedFloor,
	wallCoordinates,
	activeWallId,
	sourceWallId,
	destinationWallId,
	highlightCoordinates,
	focusArea,
	countertopOverlays,
	twoDJson
}: {
	selectedProduct: string;
	selected2DArea: string;
	selectedCategory: string;
	selectedFloor?: number | null;
	wallCoordinates?: any[];
	activeWallId?: string | number | null;
	sourceWallId?: string | number | null;
	destinationWallId?: string | number | null;
	highlightCoordinates?: any[];
	focusArea?: { x1: number; y1: number; x2: number; y2: number } | null;
	countertopOverlays?: Array<{ id: string; coordinates: any[]; color: string; selected?: boolean; label?: string }>;
	twoDJson: Record<string, any>;
}) {
	const normalizeAreaKey = (value: any) =>
		String(value ?? '')
			.toLowerCase()
			.replace(/[^a-z0-9]/g, '');

	// 1. load texture
	const texture: any = useLoader(TextureLoader, twoDJson.imageUrl);
	const imgW = texture.image.width;
	const imgH = texture.image.height;

	const maxWidth = 10;
	const aspect = imgW / imgH;
	const planeW = maxWidth;
	const planeH = maxWidth / aspect;

	const scaleX = planeW / imgW;
	const scaleY = planeH / imgH;

	// 2. resize ortho camera to match image or focused area
	const { camera, gl, size }: any = useThree();
	useEffect(() => {
		const aspect = imgW / imgH;
		const planeW = 10;
		const planeH = 10 / aspect;

		const canvasAspect = size.width / size.height;
		let viewW, viewH;

		if (focusArea) {
			const paddingFactor = 1.2;
			const areaW = Math.max(1, focusArea.x2 - focusArea.x1) * scaleX * paddingFactor;
			const areaH = Math.max(1, focusArea.y2 - focusArea.y1) * scaleY * paddingFactor;
			viewH = areaH;
			viewW = viewH * canvasAspect;
			if (viewW < areaW) {
				viewW = areaW;
				viewH = viewW / canvasAspect;
			}
			const focusCenterX = (focusArea.x1 + (focusArea.x2 - focusArea.x1) / 2 - imgW / 2) * scaleX;
			const focusCenterY = (imgH / 2 - (focusArea.y1 + (focusArea.y2 - focusArea.y1) / 2)) * scaleY;
			camera.position.x = focusCenterX;
			camera.position.y = focusCenterY;
		} else {
			if (canvasAspect > aspect) {
				viewH = planeH;
				viewW = planeH * canvasAspect;
			} else {
				viewW = planeW;
				viewH = planeW / canvasAspect;
			}
			camera.position.x = 0;
			camera.position.y = 0;
		}

		camera.left = -viewW / 2;
		camera.right = viewW / 2;
		camera.top = viewH / 2;
		camera.bottom = -viewH / 2;
		camera.updateProjectionMatrix();
		gl.setSize(size.width, size.height);
	}, [imgW, imgH, camera, gl, size, focusArea, scaleX, scaleY]);

	// 3. prepare shapes
	const area = useMemo(() => {
		const areas = Array.isArray(twoDJson?.areas) ? twoDJson.areas : [];
		if (!areas.length) return null;
		const normalizedAreaName = normalizeAreaKey(selected2DArea);
		const byName = areas.filter((entry: any) => normalizeAreaKey(entry?.areaName) === normalizedAreaName);
		const candidates = byName.length ? byName : areas;
		if (selectedFloor === null || selectedFloor === undefined) return candidates[0] || null;
		return (
			candidates.find((entry: any) => Number(entry?.floor ?? 0) === Number(selectedFloor)) || candidates[0] || null
		);
	}, [twoDJson?.areas, selected2DArea, selectedFloor]);

	const catKey = useMemo(() => {
		return selectedCategory?.replace(/Quotation$/i, '')?.toLowerCase();
	}, [selectedCategory]);

	const shapes = useMemo(() => {
		if (highlightCoordinates && highlightCoordinates.length > 0) {
			return highlightCoordinates
				.map((coord: any, index: number) => {
					const x1 = Number(coord?.x1);
					const y1 = Number(coord?.y1);
					const x2 = Number(coord?.x2);
					const y2 = Number(coord?.y2);
					if ([x1, y1, x2, y2].some(value => Number.isNaN(value))) return null;
					return {
						id: coord?.piece_type ?? coord?.pieceType ?? coord?.id ?? `${selectedProduct}-${index}`,
						x: Math.min(x1, x2),
						y: Math.min(y1, y2),
						width: Math.abs(x2 - x1),
						height: Math.abs(y2 - y1)
					};
				})
				.filter(Boolean) as Array<{ id: any; x: number; y: number; width: number; height: number }>;
		}
		if (wallCoordinates && wallCoordinates.length > 0) return [];
		if (!area) return [];
		// get products array for that category
		const products = (area.categories as any)[catKey] as Array<{ productName: string; coordinates: Array<any> }>;
		if (!products) return [];
		// find the selected product
		const item = products.find(p => p.productName === selectedProduct);
		if (!item) return [];
		// return all coordinate boxes for this product
		return item.coordinates.map(coord => ({
			id: selectedProduct,
			x: coord.x1,
			y: coord.y1,
			width: coord.x2 - coord.x1,
			height: coord.y2 - coord.y1
		}));
	}, [area, catKey, selectedProduct, wallCoordinates, highlightCoordinates]);

	const positioned = useMemo(() => {
		return shapes?.map(s => ({
			...s,
			width: s.width * scaleX,
			height: s.height * scaleY,
			position: [(s.x + s.width / 2 - imgW / 2) * scaleX, (imgH / 2 - (s.y + s.height / 2)) * scaleY, 0.1]
		}));
	}, [shapes, imgW, imgH, scaleX, scaleY]);

	const overlayPositioned = useMemo(
		() =>
			(countertopOverlays ?? []).flatMap(overlay =>
				(overlay.coordinates ?? []).flatMap((coord: any, idx: number) => {
					const x1 = Number(coord?.x1);
					const y1 = Number(coord?.y1);
					const x2 = Number(coord?.x2);
					const y2 = Number(coord?.y2);
					if ([x1, y1, x2, y2].some(value => Number.isNaN(value))) return [];
					const width = Math.abs(x2 - x1);
					const height = Math.abs(y2 - y1);
					return [
						{
							...overlay,
							key: `${overlay.id}-${idx}`,
							width: width * scaleX,
							height: height * scaleY,
							position: [
								(Math.min(x1, x2) + width / 2 - imgW / 2) * scaleX,
								(imgH / 2 - (Math.min(y1, y2) + height / 2)) * scaleY,
								0.2
							]
						}
					];
				})
			),
		[countertopOverlays, imgW, imgH, scaleX, scaleY]
	);

	const wallShapes = useMemo(() => {
		if (!wallCoordinates || wallCoordinates.length === 0) return [];
		return wallCoordinates
			.map((coord: any, idx: number) => {
				const x1 = Number(coord?.x1);
				const y1 = Number(coord?.y1);
				const x2 = Number(coord?.x2);
				const y2 = Number(coord?.y2);
				if ([x1, y1, x2, y2].some(value => Number.isNaN(value))) return null;
				return {
					id: coord?.wall_id ?? coord?.wallId ?? coord?.surface_id ?? coord?.surfaceId ?? idx + 1,
					x: x1,
					y: y1,
					width: x2 - x1,
					height: y2 - y1
				};
			})
			.filter(Boolean) as Array<{ id: any; x: number; y: number; width: number; height: number }>;
	}, [wallCoordinates]);
	const wallPositioned = useMemo(
		() =>
			wallShapes.map(shape => ({
				...shape,
				width: shape.width * scaleX,
				height: shape.height * scaleY,
				position: [
					(shape.x + shape.width / 2 - imgW / 2) * scaleX,
					(imgH / 2 - (shape.y + shape.height / 2)) * scaleY,
					0.1
				]
			})),
		[wallShapes, imgW, imgH, scaleX, scaleY]
	);

	const areaWidth = (area?.x2 - area?.x1) * scaleX;
	const areaHeight = (area?.y2 - area?.y1) * scaleY;
	const areaPosition: [number, number, number] = [
		(area?.x1 + (area?.x2 - area?.x1) / 2 - imgW / 2) * scaleX,
		(imgH / 2 - (area?.y1 + (area?.y2 - area?.y1) / 2)) * scaleY,
		0.05
	];
	const focusPosition: [number, number, number] | null = focusArea
		? [
				(focusArea.x1 + (focusArea.x2 - focusArea.x1) / 2 - imgW / 2) * scaleX,
				(imgH / 2 - (focusArea.y1 + (focusArea.y2 - focusArea.y1) / 2)) * scaleY,
				0.05
			]
		: null;

	return (
		<>
			<mesh>
				<planeGeometry args={[planeW, planeH]} />
				<meshBasicMaterial map={texture} toneMapped={false} />
			</mesh>

			{!countertopOverlays?.length && !wallCoordinates?.length && !highlightCoordinates?.length && area && (
				<mesh position={areaPosition}>
					<planeGeometry args={[areaWidth, areaHeight]} />
					<meshBasicMaterial transparent opacity={0.4} color="blue" />
				</mesh>
			)}

			{wallCoordinates?.length && focusPosition && (
				<mesh position={focusPosition}>
					<planeGeometry
						args={[
							Math.max(1, focusArea!.x2 - focusArea!.x1) * scaleX,
							Math.max(1, focusArea!.y2 - focusArea!.y1) * scaleY
						]}
					/>
					<meshBasicMaterial transparent opacity={0.12} color="#2B6CB0" />
				</mesh>
			)}

			{countertopOverlays?.length
				? overlayPositioned.map(({ key, width, height, position, color, selected }) => (
					<mesh key={key} position={position as [number, number, number]}>
						<planeGeometry args={[width, height]} />
						<meshBasicMaterial transparent opacity={selected ? 0.9 : 0.35} color={color} />
					</mesh>
				))
				: wallCoordinates?.length
				? wallPositioned.map(({ id, width, height, position }, idx) => {
						const isSource = String(id) === String(sourceWallId ?? activeWallId ?? '');
						const isDestination = String(id) === String(destinationWallId ?? '');
						const isSameWall = isSource && isDestination;
						const color = isSameWall || isSource ? '#FF4D4F' : isDestination ? '#4ADE80' : '#FFB347';
						const opacity = isSource || isDestination ? 0.95 : 0.25;
						return (
							<mesh key={`${id}-${idx}`} position={position}>
								<planeGeometry args={[width, height]} />
								<meshBasicMaterial transparent opacity={opacity} color={color} />
							</mesh>
						);
					})
				: positioned?.map(({ id, width, height, position }, idx) => (
						<mesh key={`${id}-${idx}`} position={position}>
							<planeGeometry args={[width, height]} />
							<meshBasicMaterial transparent opacity={0.8} color="#FF7875" />
						</mesh>
					))}
		</>
	);
}

export function TwoDView({
	selectedCategory,
	selectedProduct,
	selected2DArea,
	selectedFloor,
	wallCoordinates,
	activeWallId,
	sourceWallId,
	destinationWallId,
	highlightCoordinates,
	focusArea,
	countertopOverlays
}: {
	selectedProduct?: string;
	selected2DArea?: string;
	selectedCategory?: string;
	selectedFloor?: number | null;
	wallCoordinates?: any[];
	activeWallId?: string | number | null;
	sourceWallId?: string | number | null;
	destinationWallId?: string | number | null;
	highlightCoordinates?: any[];
	focusArea?: { x1: number; y1: number; x2: number; y2: number } | null;
	countertopOverlays?: Array<{ id: string; coordinates: any[]; color: string; selected?: boolean; label?: string }>;
}) {
	const { twoDJsonLoading: customerTwoDJsonLoading, twoDJson: customerTwoDJson } = useAppSelector(
		state => state.customer
	);
	const { twoDJsonLoading: projectTwoDJsonLoading, twoDJson: projectTwoDJson } = useAppSelector(
		state => state.project
	);
	const resolvedTwoDJson = projectTwoDJson?.imageUrl ? projectTwoDJson : customerTwoDJson;
	const isLoading =
		projectTwoDJson?.imageUrl || Object.keys(projectTwoDJson ?? {}).length
			? projectTwoDJsonLoading
			: customerTwoDJsonLoading;
	const hasImage = Boolean(resolvedTwoDJson?.imageUrl);
	return (
		<div className="flex flex-col w-full h-full bg-white rounded-lg overflow-hidden">
			<div className="relative flex-1 w-full h-full overflow-hidden">
				{isLoading ? (
					<div className="flex items-center justify-center w-full h-full">Loading...</div>
				) : !hasImage ? (
					<div className="flex items-center justify-center w-full h-full">No floorplan available.</div>
				) : (
					<Suspense
						fallback={
							<div className="w-full h-full flex items-center justify-center">
								<span>Loading floorplan…</span>
							</div>
						}
					>
						<Canvas
							orthographic
							camera={{ position: [0, 0, 10], zoom: 1 }}
							style={{ width: '100%', height: '100%' }}
						>
							<Scene
								selectedProduct={selectedProduct ?? ''}
								selected2DArea={selected2DArea ?? ''}
								selectedCategory={selectedCategory ?? ''}
								selectedFloor={selectedFloor}
								wallCoordinates={wallCoordinates}
								activeWallId={activeWallId}
								sourceWallId={sourceWallId}
								destinationWallId={destinationWallId}
								highlightCoordinates={highlightCoordinates}
								focusArea={focusArea ?? null}
								countertopOverlays={countertopOverlays}
								twoDJson={resolvedTwoDJson}
							/>
						</Canvas>
					</Suspense>
				)}
			</div>
		</div>
	);
}

export default TwoDView;
